import client from '@/shared/apis/client'
import { type YakurekiStatus } from '@/shared/types/yakurekiCore'

import { type CarePlanSummary } from './carePlanApi'

// 報告書の宛先。kind は頭書きの敬称と並びの意味だけに使う (処方医 = 先生 / それ以外 = 様)。
export type RecipientKind = 'physician' | 'care_manager' | 'other'

export interface HomecareRecipient {
  kind: RecipientKind
  // 医療機関・事業所
  facility: string
  // 氏名
  name: string
}

// 介護度の選択肢。値は報告書の頭書きの語のまま (空 = 介護保険を使っていない)。back の
// HomeCarePatient.CareLevel と同じ並び
export const CARE_LEVELS: { value: string; label: string }[] = [
  { value: '', label: 'なし（医療保険）' },
  { value: '要支援1', label: '要支援1' },
  { value: '要支援2', label: '要支援2' },
  { value: '要介護1', label: '要介護1' },
  { value: '要介護2', label: '要介護2' },
  { value: '要介護3', label: '要介護3' },
  { value: '要介護4', label: '要介護4' },
  { value: '要介護5', label: '要介護5' },
  { value: '申請中', label: '申請中' },
]

// 在宅の患者 (back の HomeCarePatient = 既存の患者に付けた在宅の札)。id は札の id。
// 氏名・カナ・生年月日・患者番号は既存の患者のもので、在宅の画面からは直さない (正はレセコン)。
// ended = 在宅の一覧から外している。last_visited_at = 前回の訪問日 (報告書がある訪問だけ。
// 生成が着地したか、薬剤師が書いたか)。
export interface HomecarePatient {
  id: string
  patient_id: string
  name: string
  name_kana: string
  birth_date: string | null
  patient_code: string
  care_level: string
  recipients: HomecareRecipient[]
  ended: boolean
  last_visited_at: string | null
}

// 登録の画面で探した店の患者。home_care_patient_id = 在宅の一覧にいるならその札の id
export interface HomecareCandidate {
  id: string
  name: string
  name_kana: string
  birth_date: string | null
  patient_code: string
  home_care_patient_id: string | null
}

// 患者のページの訪問の行。visited_at = 録音した日 (録音前は［訪問を記録］を押した日)。
export interface HomecareVisit {
  event_id: string
  status: YakurekiStatus
  visited_at: string
  // 報告書が着地した時刻。null = まだ (録音待ち・作成中・生成できなかった)
  generated_at: string | null
  // 薬剤師が報告書を書いた時刻。生成できず手で書いた訪問もこれで「報告書あり」になる
  edited_at: string | null
  // 訪問の日が、その時点で効いていた計画書の版の予定日に無い。計画書が無い時期の訪問は null
  off_plan: boolean | null
}

export interface HomecarePatientDetail extends HomecarePatient {
  visits: HomecareVisit[]
  // 計画書の最新の版 (無ければ null)
  plan: CarePlanSummary | null
}

// 在宅の画面で直せるのは介護度と宛先だけ
export type HomecarePatientInput = Partial<
  Pick<HomecarePatient, 'care_level' | 'recipients'>
>

// 報告書の頭書きと足の材料。患者に紐付く訪問だけ届く (旧い在宅の記録は null)。
// 頭書きは保存せず、出すときにこれと宛先 1 つずつから組む。
export interface HomeVisitLetterhead {
  patient: Pick<HomecarePatient, 'id' | 'name' | 'birth_date' | 'care_level'>
  recipients: HomecareRecipient[]
  visited_at: string
  pharmacist_name: string
  pharmacy: { name: string; phone_number: string }
}

const PATIENTS = '/api/home-care/patients/'

export const listHomecarePatients = async (): Promise<HomecarePatient[]> =>
  (await client.get<HomecarePatient[]>(PATIENTS)).data

export const getHomecarePatient = async (
  patientId: string
): Promise<HomecarePatientDetail> =>
  (await client.get<HomecarePatientDetail>(`${PATIENTS}${patientId}/`)).data

// 店の患者を選んで在宅の札を付ける。付いていれば今の札が返り、外していれば一覧に戻る
export const registerHomecarePatient = async (
  patientId: string
): Promise<HomecarePatientDetail> =>
  (
    await client.post<HomecarePatientDetail>(PATIENTS, {
      patient_id: patientId,
    })
  ).data

// 在宅の一覧から外す (訪問の記録は残る)
export const endHomecarePatient = async (patientId: string): Promise<void> => {
  await client.delete(`${PATIENTS}${patientId}/`)
}

export const findHomecareCandidates = async (
  query: string
): Promise<HomecareCandidate[]> =>
  (
    await client.get<HomecareCandidate[]>(
      '/api/home-care/patient-candidates/',
      {
        params: { q: query },
      }
    )
  ).data

export const patchHomecarePatient = async (
  patientId: string,
  input: HomecarePatientInput
): Promise<HomecarePatientDetail> =>
  (await client.patch<HomecarePatientDetail>(`${PATIENTS}${patientId}/`, input))
    .data

// ［訪問を記録］。録音待ちの器 (session_type=home_visit) を作り、その event_id を返す。
// 同じ人の自分の録音待ちが在れば back がそれを返す (押すたびに増えない)。
export const createHomecareVisit = async (patientId: string): Promise<string> =>
  (await client.post<{ event_id: string }>(`${PATIENTS}${patientId}/visits/`))
    .data.event_id

// 報告書の頭書きの材料。報告書がまだ無い (204) ときは null。
export const getHomeVisitLetterhead = async (
  yakurekiId: string
): Promise<HomeVisitLetterhead | null> => {
  const res = await client.get<{ letterhead: HomeVisitLetterhead | null }>(
    `/api/request-events/${yakurekiId}/home-visit-report/`
  )
  return res.data?.letterhead ?? null
}
